import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias, equal } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { SH_PAYMENT } from "sh-www/models/sh-payment";

class AdminAccountingPayout extends Controller {

  /**
   * Register services
   */
  @service('state-manager-hybrids/heartbeat-state-manager-hybrid') heartbeatStateManager;
  @service('states/routes/admin/accounting/payout') state;
  @service('state-managers/routes/admin/accounting/payout') stateManager;
  @service store;

  /**
   * @type {Number}     How many records to load.
   */
  count = 30;


  //Multi-select params
  companyOptions = [];
  userOptions = [];
  selectedUser = [];
  selectedCompany = [];
  companyFilter = {
    name: null
  };
  userFilter = {
    name: null
  };
  entityId = null;

  /**
   * This is a string that will be set with which search category has been picked by the user.
   *
   * @type {id|entityName|applicationId}
   */
  _selectedSearchCategory = '';

  /**
   * ------------------ NOTE ----------------------------
   * All these params are ALIASED to the top level state. Easiest thing to do for now to not have
   * to rewrite this entire controller.
   */
  @alias('state.paramPage') page;
  @alias('state.paramApplicationId') _applicationId;
  @alias('state.paramCompanyId') _companyId;
  @alias('state.paramUserId') _userId;
  @alias('state.paramPaymentId') _id;
  @alias('state.paramPayoneerAccountId') _payoneerAccountId;
  @alias('state.paramTransactionId') _transactionId;
  @alias('state.paramPaymentMethod') checkedPaymentMethod;
  @alias('state.paramPaymentStatus') checkedPaymentStatus;

  @equal('state.paramApproved', '0')
  needsApprovalFilterOn;

  /**
   * @type {Object}     Constant object for the ShPayment simple_status for the template
   */
  get constantsPaymentMethods() {
    return SH_PAYMENT.SIMPLE_STATUS;
  }

  get constantsPaymentStatuses() {
    return SH_PAYMENT.STATUS;
  }

  //Initial checked status of the search inputs
  @computed('_selectedSearchCategory')
  get entityNameChecked() {
    return this._selectedSearchCategory === 'entityName'
      ? 'checked'
      : '';
  }

  @computed('_selectedSearchCategory')
  get applicationIdChecked() {
    return this._selectedSearchCategory === 'applicationId'
      ? 'checked'
      : '';
  }

  @computed('_selectedSearchCategory')
  get companyIdChecked() {
    return this._selectedSearchCategory === 'companyId'
      ? 'checked'
      : '';
  }

  @computed('_selectedSearchCategory')
  get idChecked() {
    return this._selectedSearchCategory === 'id'
      ? 'checked'
      : '';
  }

  @computed('_selectedSearchCategory')
  get payoneerAccountChecked() {
    return this._selectedSearchCategory === 'payoneerAccountId'
      ? 'checked'
      : '';
  }

  @computed('_selectedSearchCategory')
  get transactionIdChecked() {
    return this._selectedSearchCategory === 'transactionId'
      ? 'checked'
      : '';
  }

  /**
   * Various states for the radio buttons for simple status filters
   */
  @computed('checkedPaymentMethod')
  get bankChecked() {
    return this.checkedPaymentMethod === SH_PAYMENT.SIMPLE_STATUS.BANK;
  }

  @computed('checkedPaymentMethod')
  get manualChecked() {
    return this.checkedPaymentMethod === SH_PAYMENT.SIMPLE_STATUS.MANUAL;
  }

  @computed('checkedPaymentMethod')
  get payoneerChecked() {
    return this.checkedPaymentMethod === SH_PAYMENT.SIMPLE_STATUS.PAYONEER;
  }

  @computed('checkedPaymentMethod')
  get paypalChecked() {
    return this.checkedPaymentMethod === SH_PAYMENT.SIMPLE_STATUS.PAYPAL;
  }

  @computed('checkedPaymentMethod')
  get pendingChecked() {
    return this.checkedPaymentMethod === SH_PAYMENT.SIMPLE_STATUS.PENDING;
  }

  @computed('checkedPaymentMethod')
  get hyperwalletChecked() {
    return this.checkedPaymentMethod === SH_PAYMENT.SIMPLE_STATUS.HYPERWALLET;
  }

  /**
   * Various states for the radio buttons for payment status filters
   */
  @computed('checkedPaymentStatus')
  get dueChecked() {
    return this.checkedPaymentStatus === SH_PAYMENT.STATUS.DUE;
  }

  @computed('checkedPaymentStatus')
  get paidChecked() {
    return this.checkedPaymentStatus === SH_PAYMENT.STATUS.PAID;
  }

  @computed('checkedPaymentStatus')
  get errorChecked() {
    return this.checkedPaymentStatus === SH_PAYMENT.STATUS.ERROR;
  }

  @computed('heartbeatStateManager.adminBag.billable_needs_approval_count')
  get _needsApprovalCount() {
    return this.heartbeatStateManager.adminBag?.billable_needs_approval_count ?? 0;
  }

  @computed('needsApprovalFilterOn','_needsApprovalCount')
  get _needsApprovalButtonText() {
    return this.needsApprovalFilterOn
      ? 'Show All Approval Statuses'
      : `Needs Approval (${this._needsApprovalCount})`;
  }

  @computed('state.queryParams')
  get _hasAnyFilters() {
    const queryParams = this.state.queryParams;
    const filterCount = Object.keys(queryParams).reduce((count, key) => {
      if (
        queryParams[key] == null
        || queryParams[key] === ''
      ) {
        return count;
      }
      return count + 1;
    }, 0);

    return filterCount > 0;
  }

  /**
   * Get a new filter object for the server.
   *
   * @param {Object} queryParams
   * @return {Object}
   */
  getNewFilter(queryParams) {
    const filter = {};

    if (queryParams.approved) {
      filter['approved'] = queryParams.approved;
    }

    if (queryParams.applicationId) {
      filter['applicationId'] = queryParams.applicationId;
    }

    if (queryParams.companyId) {
      filter['companyId'] = queryParams.companyId;
    }

    if (queryParams.id) {
      filter['id'] = queryParams.id;
    }

    if (queryParams.userId) {
      filter['userId'] = queryParams.userId;
    }

    if (queryParams.entityName) {
      filter['entityName'] = queryParams.entityName;
    }

    if (queryParams.payoneerAccountId) {
      filter['payoneerAccountId'] = queryParams.payoneerAccountId;
    }

    if (queryParams.transactionId) {
      filter['transactionId'] = queryParams.transactionId;
    }

    if (queryParams.paymentMethod) {
      filter['simpleStatus'] = queryParams.paymentMethod;
    }

    if (queryParams.due) {
      filter['due'] = 1;
      filter['paid'] = 0;
    }

    if (queryParams.error) {
      filter['error'] = 1;
      filter['paid'] = 0;
    }

    if (queryParams.paid) {
      filter['paid'] = 1;
    }

    return filter;
  }

  /**
   * Given the query params, set the internal private state on page load.
   *
   * @param {Object} params
   */
  updateStateFromParams(params) {
    if (params.applicationId) {
      this.set('_applicationId', params.applicationId);
      this.set('_selectedSearchCategory', 'applicationId');
    }

    if (params.companyId) {
      this.set('_companyId', params.companyId);
      this.set('_selectedSearchCategory', 'companyId');
    }

    if (params.id) {
      this.set('_id', params.id);
      this.set('_selectedSearchCategory', 'id');
    }

    if (params.userId) {
      this.set('_userId', params.userId);
    }

    if (params.page) {
      this.set('page', params.page);
    }

    if (params.payoneerAccountId) {
      this.set('_payoneerAccountId', params.payoneerAccountId);
    }

    if (params.transactionId) {
      this.set('_transactionId', params.transactionId);
    }

    if (params.paymentMethod) {
      this.set('checkedPaymentMethod', params.paymentMethod);
    }

    if (params.due) {
      this.set('checkedPaymentStatus', SH_PAYMENT.STATUS.DUE);
    }

    if (params.paid) {
      this.set('checkedPaymentStatus', SH_PAYMENT.STATUS.PAID);
    }

    if (params.error) {
      this.set('checkedPaymentStatus', SH_PAYMENT.STATUS.ERROR);
    }
  }

  @action
  clearFilters() {
    this.set('showFilterCard', false);
    this.state.init();
    this.stateManager.navigateToPaymentsPage(1);
  }

  @action
  toggleFilterNeedsApproval() {
    this.setProperties({
      'showFilterCard': false,
      'state.paramApproved': this.needsApprovalFilterOn ? undefined : '0',
    });
    this.stateManager.navigateToPaymentsPage(1);
  }

  /**
   * @param {String} applicationId
   */
  @action
  onInputApplicationId(applicationId) {
    this.set('_applicationId', applicationId);
  }

  /**
   * @param {String} accountId
   */
  @action
  onInputPayoneerAccountId(accountId) {
    this.set('_payoneerAccountId', accountId);
  }

  /**
   * @param {String} paymentId
   */
  @action
  onInputPaymentId(paymentId) {
    this.set('_id', paymentId);
  }

  /**
   * @param {String} transactionId
   */
  @action
  onInputTransactionId(transactionId) {
    this.set('_transactionId', transactionId);
  }

  /**
   * @param {Company} company
   */
  @action
  onCompanySelected(company) {
    this.set('_companyId', company.id);
  }

  /**
   * Called when the admin selects a user from the dropdown
   *
   * @param {User} user
   */
  @action
  onUserEntitySelected(user) {
    this.set('_userId', user.id);
  }

  /**
   * Called by the filter form to do a new search with inputs.
   */
  @action
  searchPayments() {
    this.set('showFilterCard', false);
    this.stateManager.navigateToPaymentsPage(1);
  }

  @action
  searchForResource(type, filter, callback) {
    let filterData = {};

    if (filter) {
      for (const key in filter) {
        // skip loop if the property is from prototype
        if (!filter.hasOwnProperty(key)) {
          continue;
        }
        filterData[key] = filter[key];
      }
    }

    filterData = {
      filter: filterData
    };

    return this.store.query(type, filterData).then(models => {
      if (typeof callback === 'function') {
        callback(models);
      }
      return models;
    });
  }

  /**
   * @param {String} category
   * @param e
   */
  @action
  setSearchCategory(category, e) {
    const currentSearchCategorySelected = this._selectedSearchCategory;

    this.setProperties({
      _id: undefined,
      _applicationId: undefined,
      _companyId: undefined,
      _payoneerAccountId: undefined,
      _userId: undefined,
      selectedUser: [],
      userOptions: [],
      selectedCompany: [],
      companyOptions: [],
      _transactionId: undefined
    });

    this.set('_selectedSearchCategory', category === currentSearchCategorySelected ? '' : category);
  }

  /**
   * @param {String} method
   * @param {MouseEvent} e
   */
  @action
  setSearchMethod(method, e) {
    this.set('checkedPaymentMethod', this.checkedPaymentMethod === method ? undefined : method);
  }

  @action
  toggleShowFilter() {
    this.toggleProperty('showFilterCard');
  }
}

export default AdminAccountingPayout;
